<template>
  <div
    ref="elementRef"
    :class="className"
    :style="{
      opacity: inView ? 1 : initialOpacity,
      transition: `opacity ${duration}ms ${easing}, filter ${duration}ms ${easing}`,
      filter: blur ? (inView ? 'blur(0px)' : 'blur(10px)') : 'none'
    }"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, useTemplateRef, watch } from 'vue';

interface Props {
  blur?: boolean;
  duration?: number;
  easing?: string;
  delay?: number;
  threshold?: number;
  initialOpacity?: number;
  className?: string;
  resetOnPageChange?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  blur: false,
  duration: 1000,
  easing: 'ease-out',
  delay: 0,
  threshold: 0.1,
  initialOpacity: 0,
  className: '',
  resetOnPageChange: true
});

const inView = ref(false);
const elementRef = useTemplateRef<HTMLDivElement>('elementRef');
let observer: IntersectionObserver | null = null;

// 监听页面滚动位置变化，重置动画状态
const resetAnimation = () => {
  inView.value = false;
  if (observer && elementRef.value) {
    observer.observe(elementRef.value);
  }
};

// 监听页面滚动事件，当页面位置变化时重置动画
const handleScroll = () => {
  if (props.resetOnPageChange) {
    resetAnimation();
  }
};

onMounted(() => {
  const element = elementRef.value;
  if (!element) return;

  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        observer?.unobserve(element);
        setTimeout(() => {
          inView.value = true;
        }, props.delay);
      }
    },
    { threshold: props.threshold }
  );

  observer.observe(element);
  
  // 监听页面滚动事件
  if (props.resetOnPageChange) {
    window.addEventListener('scroll', handleScroll);
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
  
  // 移除滚动监听
  if (props.resetOnPageChange) {
    window.removeEventListener('scroll', handleScroll);
  }
});
</script>
